import React, { useMemo } from 'react'
import { connect } from 'react-redux'
import { SubmissionError } from 'redux-form'
import { useTranslation } from 'react-i18next'
import { toast, cssTransition } from 'react-toastify'
import QS from 'qs'

import {
  setCurrentUserToken,
  setCurrentUserAuthProvider,
} from 'ducks/users/index.ts'

import { parseJWT } from 'utils/auth'
import { signup, signupWithGoogle } from 'utils/io'
import { getReferral } from 'utils/rewardful'
import { trackSignupEvent } from 'utils/facebookPixelEvents'
import { getAttributionCookies } from 'utils/attributionCookies'

import AuthPage from './AuthV2/AuthPage.tsx'
import SignupForm from './AuthV2/SignupForm.tsx'

const Signup = props => {
  const {
    location,
    children,
    history,
    setCurrentUserToken,
    setCurrentUserAuthProvider,
  } = props

  const { i18n } = useTranslation()

  const { initialValues, error, inviteToken, redirect } = useMemo(() => {
    const { location } = props
    const queryParams = QS.parse(location.search, { ignoreQueryPrefix: true })
    const { token, redirect } = queryParams

    let initialValues = {}
    let error = null

    if (token) {
      const tokenBody = parseJWT(token)

      if (tokenBody) {
        initialValues = { email: tokenBody.email }
      } else {
        error = 'Your invitation has expired. Invitations expire after 7 days.'
      }
    }

    return { initialValues, error, inviteToken: token, redirect }
  }, [location])

  const handleSubmit = values =>
    new Promise((resolve, reject) => {
      const rewardfulToken = getReferral()

      const region = i18n.language === 'ja' ? 'japan' : null

      const data = {
        ...values,
        inviteToken,
        rewardfulToken,
        region,
        ...getAttributionCookies(),
      }

      signup(
        data,
        ({
          success,
          errors,
          sessionToken,
          newOrg,
          validateEmail,
          lastUsedAuthProvider,
        }) => {
          if (!success) {
            return reject(new SubmissionError(errors))
          }

          setCurrentUserToken(sessionToken)
          setCurrentUserAuthProvider(lastUsedAuthProvider)
          trackSignupEvent()

          const nextPage = validateEmail ? 'email-validation' : ''

          let redirectPage = `/${nextPage}`

          if (inviteToken) {
            redirectPage = `/${nextPage}?token=${inviteToken}`
          }

          if (redirectPage) {
            if (!String(redirectPage).startsWith('/')) {
              redirectPage = `/${redirectPage}`
            }

            history.push(redirectPage)
          } else if (newOrg) {
            const newOrgNextPage = '/email-validation'
            history.push(newOrgNextPage)
          } else {
            history.push('/')
          }

          resolve()
        }
      )
    })
  const handleSubmitWithGoogle = async idToken => {
    try {
      if (!idToken) return alert('No Google credential')

      const data = await signupWithGoogle({
        idToken,
        ...getAttributionCookies(),
      })

      setCurrentUserToken(data.sessionToken)
      setCurrentUserAuthProvider(data.lastUsedAuthProvider)

      if (inviteToken) {
        history.push(`/?token=${inviteToken}`)

        return
      }

      history.push('/')
    } catch (err) {
      toast(
        <div>
          <p>Failed to sign up with Google.</p>
        </div>,
        {
          position: 'bottom-right',
          className: 'token-expired__toast',
          bodyClassName: 'token-expired__toast-body',
          hideProgressBar: true,
          autoClose: 5000,
          transition: cssTransition({
            enter: 'animate__animated animate__fadeInUp',
            exit: 'animate__animated animate__fadeOutDown',
            collapse: false,
          }),
        }
      )
    }
  }

  const isInvite = location.pathname === '/accept-invite'

  return (
    <AuthPage name="signup-page" error={error} version={2}>
      <SignupForm
        isInvite={isInvite}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        handleSubmitWithGoogle={handleSubmitWithGoogle}
        redirect={redirect}
      />
      {children}
    </AuthPage>
  )
}

export default connect(null, {
  setCurrentUserToken,
  setCurrentUserAuthProvider,
})(Signup)
